import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import { api, setStoredUser } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Sparkles, Gift, Award, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { PublicHeader, Footer } from '@/components/layout/PublicHeader';
import { fmtTimeAgo, fmtMoney } from '@/lib/realtime';

export default function LoyaltyPage() {
  const { user, setUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [redeeming, setRedeeming] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/loyalty/me'); setInfo(data); } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const redeem = async () => {
    setRedeeming(true);
    try {
      const { data } = await api.post('/loyalty/redeem');
      toast.success(`Coupon ${data.coupon.code} created — ${fmtMoney(data.coupon.discount_amount)} off`);
      load();
      // refresh user points
      const me = await api.get('/auth/me');
      setUser(me.data); setStoredUser(me.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Redeem failed'); } finally { setRedeeming(false); }
  };

  const tierColor = {
    Bronze: 'bg-amber-100 text-amber-900 border-amber-200',
    Silver: 'bg-slate-100 text-slate-900 border-slate-200',
    Gold: 'bg-yellow-100 text-yellow-900 border-yellow-200',
    Platinum: 'bg-violet-100 text-violet-900 border-violet-200',
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <main className="mx-auto max-w-3xl space-y-4 px-4 py-6 sm:px-6">
        <Card>
          <CardHeader>
            <CardTitle className="font-display text-2xl">Loyalty & rewards</CardTitle>
            <CardDescription>Earn 1 point per {fmtMoney(1)} spent. Redeem 100 points for a {fmtMoney(5)} coupon.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Your balance</p>
                <p data-testid="loyalty-points-value" className="font-display text-5xl font-semibold tabular text-primary">{info?.points ?? user?.loyalty_points ?? 0}</p>
                <p className="text-xs text-muted-foreground">points</p>
              </div>
              <Badge data-testid="loyalty-tier-badge" className={`border text-base ${tierColor[info?.vip_level || user?.vip_level || 'Bronze']}`}>
                <Award className="mr-1 h-4 w-4" /> {info?.vip_level || user?.vip_level || 'Bronze'} VIP
              </Badge>
            </div>
            {info?.next_tier && info.next_tier.points_needed > 0 && (
              <div className="mt-4">
                <p className="text-xs text-muted-foreground">{info.next_tier.points_needed} pts to {info.next_tier.tier}</p>
                <Progress value={Math.min(100, (info.points / (info.points + info.next_tier.points_needed)) * 100)} className="mt-1 h-2" />
              </div>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <Button data-testid="loyalty-redeem-button" disabled={!info || info.points < (info?.redeem_increment || 100) || redeeming} onClick={redeem}>
                <Sparkles className="mr-1 h-4 w-4" /> Redeem 100 pts → {fmtMoney(5)} coupon
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Available coupons</CardTitle></CardHeader>
          <CardContent>
            {!info?.coupons?.length && <p className="text-sm text-muted-foreground">No active coupons. Redeem points to get one.</p>}
            <ul className="grid gap-2">
              {(info?.coupons || []).map((c) => (
                <li key={c.id} data-testid="loyalty-coupon-row" className="flex items-center justify-between rounded border border-border bg-secondary px-3 py-2">
                  <div>
                    <p className="font-mono text-sm font-semibold">{c.code}</p>
                    <p className="text-xs text-muted-foreground">{fmtMoney(c.discount_amount)} off</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard?.writeText(c.code); toast.success('Copied'); }}><Copy className="mr-1 h-4 w-4" /> Copy</Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>History</CardTitle></CardHeader>
          <CardContent>
            <ul className="divide-y divide-border">
              {(info?.transactions || []).map((t) => (
                <li key={t.id} className="flex items-center justify-between py-2 text-sm">
                  <span><Gift className="mr-2 inline h-4 w-4 text-primary" /> {t.note}</span>
                  <span className={`tabular ${t.points > 0 ? 'text-emerald-700' : 'text-amber-700'}`}>{t.points > 0 ? `+${t.points}` : t.points}</span>
                </li>
              ))}
              {!info?.transactions?.length && <li className="py-4 text-center text-xs text-muted-foreground">No activity yet.</li>}
            </ul>
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
